import { useEffect, useRef, useState } from "react";

// One IntersectionObserver for every photo in the conversation, so a chat with hundreds of photos
// keeps one observer instead of hundreds. A photo counts as in view a screen before it scrolls in,
// so it is usually ready by the time it shows. Once seen it stays loaded.

const MARGIN = "600px 0px";
type Watch = (seen: boolean) => void;

let observer: IntersectionObserver | null = null;
const watchers = new Map<Element, Watch>();

function shared(): IntersectionObserver | null {
  if (typeof IntersectionObserver === "undefined") return null;
  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const watch = watchers.get(entry.target);
        if (!watch) continue;
        watchers.delete(entry.target);
        observer?.unobserve(entry.target);
        watch(true);
      }
    },
    { rootMargin: MARGIN },
  );
  return observer;
}

/** For tests: forget the shared observer so the next one is made from the current global. */
export function resetInView(): void {
  observer?.disconnect();
  observer = null;
  watchers.clear();
}

/**
 * Whether the element has come near the screen. Where the browser has no IntersectionObserver,
 * everything counts as in view, as photos did before.
 */
export function useInView<T extends Element>(enabled = true) {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const el = ref.current;
    if (!enabled || seen || !el) return;
    const io = shared();
    if (!io) {
      setSeen(true);
      return;
    }
    watchers.set(el, setSeen);
    io.observe(el);
    return () => {
      watchers.delete(el);
      io.unobserve(el);
    };
  }, [enabled, seen]);
  return { ref, seen };
}
